import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toastSuccess } from '../utils/toasts';
import Input from './atoms/Input';
import Switch from './atoms/Switch';
import Button from './atoms/Button';
import {
  useCreateCouponMutation,
  useUpdateCouponMutation,
} from '../store';
import CouponDTO, { CreateCouponDTO } from '../types/Coupon';
import useGetParams from '../hooks/useGetParams';

interface Props {
  coupon?: CouponDTO;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  code: string;
  isActive: boolean;
  quantity: number;
  value: number;
}

const noSpaces = (value: string) => !/\s/.test(value);

const validationSchema = yup.object<FormData>().shape({
  isActive: yup.boolean().required(),
  code: yup.string().required('Debes ingresar un código').test('no-spaces', 'No se permiten espacios', noSpaces),
  quantity: yup
    .number()
    .required('Debes ingresar una cantidad válida')
    .typeError('Debes ingresar un número'),
  value: yup
    .number()
    .min(1, 'Debes ingresar un valor mayor a 1')
    .max(100, 'Debes ingresar un valor que no supere 100')
    .required('Debes ingresar un número')
    .typeError('Debes ingresar un valor válido'),
});

const CreateEditCoupon: React.FC<Props> = ({ coupon, onSuccess, onCancel }) => {
  const defaultCouponValue = (formCoupon: CouponDTO | undefined) => {
    if (!formCoupon) {
      return undefined;
    }
    if (formCoupon.type === 'percentage') {
      return formCoupon.value * 100;
    }
    return formCoupon.value;
  };

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      isActive: coupon ? coupon.active : true,
      code: coupon?.code,
      quantity: coupon?.limit_amount,
      value: defaultCouponValue(coupon),
    },
    resolver: yupResolver<FormData>(validationSchema),
  });

  const { eventId, businessId } = useGetParams();

  const [createCoupon, resCreateCoupon] = useCreateCouponMutation();
  const [updateCoupon, resUpdateCoupon] = useUpdateCouponMutation();

  useEffect(() => {
    if (resCreateCoupon.isSuccess || resUpdateCoupon.isSuccess) {
      onSuccess();
      toastSuccess('Cupón creado correctamente');
    }
  }, [resCreateCoupon.isSuccess, resUpdateCoupon.isSuccess]);

  const parseFormToDto = (data: FormData): CreateCouponDTO => ({
    code: data.code,
    value: data.value / 100,
    limit_amount: data.quantity,
    active: data.isActive,
    type: 'percentage',
  });

  const onSubmit = async (data: FormData) => {
    const parsedCoupon = parseFormToDto(data);
    if (coupon) {
      updateCoupon({
        coupon: { ...parsedCoupon, id: coupon.id },
        eventId,
        businessId,
      });
    } else {
      createCoupon({
        coupon: parsedCoupon,
        eventId,
        businessId,
      });
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full flex flex-col gap-4">
        <div>
          <Switch
            label="Cupón habilitado para la venta"
            labelClassName="text-text-primary"
            checked={watch('isActive')}
            onChange={(e) => setValue('isActive', e.target.checked)}
          />
        </div>

        <Input
          {...register('code', { required: 'This field is required' })}
          id="name"
          label="Nombre"
          labelAbove={true}
          errMsg={formState.errors.code?.message}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('quantity', { required: true })}
            id="quantity"
            type="number"
            label="Cantidad"
            labelAbove={true}
            errMsg={formState.errors.quantity?.message}
          />
          <Input
            {...register('value', { required: true })}
            id="value"
            type="number"
            label="% de descuento"
            labelAbove={true}
            step={0.0001}
            errMsg={formState.errors.value?.message}
          />
        </div>
        <div className="flex justify-between gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={resCreateCoupon?.isLoading || resUpdateCoupon.isLoading}
          >
            Guardar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateEditCoupon;
